/**
 * COPY STRATEGY — FuelAccountingPage
 *
 * Awareness:      Solution-Aware (Stage 3/5)
 *                 Reader knows fuel is a deductible expense but doesn't know
 *                 which of the 3 Lithuanian methods to use or how they compare.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 Three specific mechanisms compared: actual expenses, norma,
 *                 and business fuel card. Each with pros, cons, and requirements.
 *
 * Avatar:         Active driver spending €200–500/month on fuel.
 *                 Already using actual expenses or considering the switch.
 *                 Wants to maximize fuel deductions specifically.
 *
 * Hook:           Money left on table — "€300-500/mėn. kurui. Kiek susigrąžinate?"
 * Structure:      3 methods compared → Pros/cons each → Which to choose → CTA
 * Proof:          Specific method requirements, document types, calculation examples
 * Funnel:         Pairs with FR0457 and ExpenseNormPage
 */
import { motion } from 'framer-motion'
import { PiWarning, PiCheckCircle } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const FuelAccountingPage = () => {
  usePageSetup('Kuro apskaita ir išlaidos 2026: 3 metodai, kurie sutaupo šimtus eurų', {
    scroll: true,
    description: 'Leidžiate €300–500/mėn. kurui — kiek susigrąžinate? 3 kuro apskaitos metodai Lietuvoje: faktinės išlaidos, 30% norma ir km normatyvai. Palyginimas su pavyzdžiais.',
    type: 'article',
    datePublished: '2025-12-18',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Kuro apskaita 2026: 3 metodai, kurie sutaupo šimtus eurų
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Išlaidos</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">8 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Vidutinis pavežėjas išleidžia €300-500/mėn. kurui. Kiek iš to susigrąžinate?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-8">
          Tinkama kuro apskaita gali atgauti šimtus eurų mokesčių. Lietuvoje galite rinktis iš trijų metodų — kiekvienas turi savo privalumus ir trūkumus.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Trys kuro apskaitos metodai
        </MotionText>

        <MotionDiv className="space-y-5 mb-6">
          <MotionDiv className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded-r-xl">
            <MotionText as="h3" className="text-blue-900 text-base font-medium mb-2">
              1. Faktinių išlaidų metodas
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-sm mb-2">
              Fiksuojate visas faktines kuro išlaidas su kvitais. Reikalingas degalų žurnalas ir dokumentai su IV numeriu.
            </MotionText>
            <MotionText as="p" className="text-blue-700 text-xs">
              ✅ Tinka: kai išlaidos viršija 30% pajamų, daug važinėjate
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-xl">
            <MotionText as="h3" className="text-green-900 text-base font-medium mb-2">
              2. 30% išlaidų norma
            </MotionText>
            <MotionText as="p" className="text-green-800 text-sm mb-2">
              Automatiškai deklaruojate 30% pajamų kaip išlaidas. Nereikia rinkti kvitų ar vesti žurnalo.
            </MotionText>
            <MotionText as="p" className="text-green-700 text-xs">
              ✅ Tinka: mažos išlaidos, nenorite tvarkyti dokumentų, paprasta apskaita
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-purple-500 bg-purple-50 rounded-r-xl">
            <MotionText as="h3" className="text-purple-900 text-base font-medium mb-2">
              3. Kilometrų normatyvai (retas metodas)
            </MotionText>
            <MotionText as="p" className="text-purple-800 text-sm mb-2">
              Skaičiuojama pagal nuvažiuotų kilometrų kiekį. Lietuvoje retai naudojamas.
            </MotionText>
            <MotionText as="p" className="text-purple-700 text-xs">
              ⚠️ Sudėtingas - reikia derinti su VMI
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          1 metodas: Faktinių išlaidų apskaita
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-5">
          Šis metodas leidžia atskaityti faktiškai išleistas sumas kuro įsigijimui. Reikalauja kruopštaus dokumentų tvarkymo.
        </MotionText>

        <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-3">
          Degalų žurnalas - ką fiksuoti:
        </MotionText>

        <MotionDiv className="space-y-2 mb-5">
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Data ir laikas</strong> kiekvieno pirkimo
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Degalų kiekis</strong> (litrai) ir suma
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Spidometro rodmenys</strong> (pradžios ir pabaigos)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Veiklos ir asmeniniai km</strong> (procentinis padalinimas)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              <strong>Degalinės pavadinimas</strong> ir vieta
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Kvitų reikalavimai
              </MotionText>
              <MotionText as="p" className="text-amber-800 text-sm mb-3">
                Kiekviename kuro kvite BŪTINAI turi būti:
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">• Jūsų vardas ir pavardė</li>
                <li className="text-amber-800 text-sm">• IV numeris arba asmens kodas</li>
                <li className="text-amber-800 text-sm">• Suma ir degalų kiekis (litrai)</li>
                <li className="text-amber-800 text-sm">• Data ir degalinės duomenys</li>
              </ul>
              <MotionText as="p" className="text-amber-700 text-xs mt-3">
                ⚠️ Be šių duomenų kvitas nebus pripažįstas išlaida!
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-3">
          Kaip skaičiuoti verslo dalį?
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            Galite atskaityti tik tą kuro dalį, kuri buvo naudota veiklai (ne asmeniniams reikalams).
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-medium mb-3">
              Pavyzdys:
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Iš viso nuvažiuota: 3 000 km/mėn
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Darbui (Bolt): 2 400 km (80%)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Asmeniniams: 600 km (20%)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2">
              Kuras už: 400€
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono font-medium text-green-700">
              Atskaityti galima: 320€ (80%)
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          2 metodas: 30% išlaidų norma
        </MotionText>

        <MotionDiv className="p-4 bg-green-50 border border-green-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-green-900 text-base font-medium mb-3">
            Paprasčiausias variantas
          </MotionText>
          <MotionText as="p" className="text-green-800 text-sm mb-4">
            VMI automatiškai priima, kad 30% jūsų pajamų sudaro išlaidos (įskaitant kurą, draudimą, remontą ir kt.). Nereikia:
          </MotionText>
          <ul className="space-y-2">
            <li className="text-green-800 text-sm">✅ Rinkti kuro kvitų</li>
            <li className="text-green-800 text-sm">✅ Vesti degalų žurnalo</li>
            <li className="text-green-800 text-sm">✅ Skaičiuoti verslo/asmeninių km</li>
            <li className="text-green-800 text-sm">✅ Saugoti dokumentų 10 metų</li>
          </ul>
        </MotionDiv>

        <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-3">
          Kada verta rinktis 30% normą?
        </MotionText>

        <MotionDiv className="space-y-3 mb-8">
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Mažos faktinės išlaidos (kuras + remontas &lt; 30% pajamų)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Nenorite tvarkyti dokumentų ir apskaitos
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Vairuojate elektromobilį (beveik nėra kuro išlaidų)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Dirbate ne visa darbo diena
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kuris metodas naudingesnis?
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-blue-900 text-base font-medium mb-3">
            Palyginimas
          </MotionText>
          <MotionDiv className="space-y-4">
            <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
                Scenarijus 1: Didelės išlaidos
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono mb-1">
                Pajamos: 2 000€
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono mb-1">
                Faktinės išlaidos: 900€ (45%)
              </MotionText>
              <MotionText as="p" className="text-green-700 text-sm font-medium mt-2">
                → FAKTINĖS IŠLAIDOS naudingesnis (sutaupoma apie 300 €/metus mokesčių)
              </MotionText>
            </MotionDiv>

            <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
                Scenarijus 2: Mažos išlaidos
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono mb-1">
                Pajamos: 2 000€
              </MotionText>
              <MotionText as="p" className="text-zinc-700 text-sm font-mono mb-1">
                Faktinės išlaidos: 400€ (20%)
              </MotionText>
              <MotionText as="p" className="text-green-700 text-sm font-medium mt-2">
                → 30% NORMA naudingesnis (gausite 600€ atskaitą vietoj 400€)
              </MotionText>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Dokumentų saugojimas
        </MotionText>

        <MotionDiv className="space-y-3 mb-8">
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              📁 Kvitus saugoti <strong>10 metų</strong> (MAĮ 37 str.)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              📱 Nufotografuokite kvitus - popierius gali išblukti
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              💾 Saugokite elektroninėje sistemoje arba debesyje
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              📊 Degalų žurnalą galite vesti Excel ar specialioje apskaitoje
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
            💡 Patarimas
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-sm">
            Pirmaisiais metais rinkite visus kuro kvitus ir skaičiuokite faktines išlaidas. Po metų pamatysite, ar jūsų išlaidos viršija 30%. Tuomet galėsite nuspręsti, kuris metodas jums naudingesnis ateityje.
          </MotionText>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos?' },
          { slug: '60-procentu-islaidos-strategija', title: '60% faktinės išlaidos: strategija €45k pajamoms' },
          { slug: 'bolt-apskaita-dokumentai', title: 'Bolt apskaita: 3 dokumentai, kuriuos PRIVALOTE atsisiųsti' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default FuelAccountingPage
